import { Check, Info } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function Steps({ children, label }: { children: ReactNode; label: string }) {
  return (
    <ol aria-label={label} className="flex flex-col">
      {children}
    </ol>
  );
}

/** One numbered step of the quickstart. The rail line connects consecutive steps. */
export function Step({ n, id, title, description, done, last, aside, children }: { n: number; id: string; title: ReactNode; description?: ReactNode; done?: boolean; last?: boolean; aside?: ReactNode; children: ReactNode }) {
  return (
    <li id={id} aria-labelledby={`${id}-title`} className="relative grid scroll-mt-6 grid-cols-[28px_minmax(0,1fr)] gap-x-4 pb-10 last:pb-0">
      {!last ? <span aria-hidden className="absolute bottom-2 left-[13.5px] top-9 w-px bg-border" /> : null}
      <span
        aria-hidden
        className={cn(
          "relative mt-0.5 grid size-7 place-items-center rounded-full border text-[12px] font-semibold tabular [&_svg]:size-3.5",
          done ? "border-accent/40 bg-accent-soft text-accent" : "border-border-strong bg-surface-2 text-fg-2",
        )}
      >
        {done ? <Check strokeWidth={3} /> : n}
      </span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 id={`${id}-title`} className="text-[14px] font-semibold leading-7 text-fg">
              <span className="sr-only">Step {n}: </span>
              {title}
              {done ? <span className="sr-only"> (done)</span> : null}
            </h2>
            {description ? <p className="max-w-3xl text-[13px] text-fg-3">{description}</p> : null}
          </div>
          {aside}
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </li>
  );
}

export function Callout({ icon, children, className }: { icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex gap-2 rounded-md border bg-surface-2/60 px-3 py-2 text-xs leading-relaxed text-fg-2 [&_code]:font-mono [&_code]:text-[11.5px] [&_code]:text-fg", className)}>
      <span className="mt-px shrink-0 text-fg-3 [&_svg]:size-3.5">{icon ?? <Info />}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Small uppercase label above a code block. */
export function BlockLabel({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">{children}</span>
      {right}
    </div>
  );
}
